import { useState } from "react";
import validateFields from "../ValidationChecker";
import AuthForm from "../common/authForm/AuthForm";
import CustomOrgChart from "../common/hierarchy/CustomOrgChart";
import InviteMembers from "./invite/InviteMembers";
import { Col, Container, Row } from "react-bootstrap";
import SignupLeftSideCard from "../common/cards/SignupLeftSideCard";
import Stepper from "../common/authForm/Stepper";
import { scrollToError } from "../../utils/helper";
import { Link } from "react-router-dom";


const BusinessRegisterForm = () => {
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(1);
    const [formValues, setFormValues] = useState({});
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [touched, setTouched] = useState({});

    const customFields = [
        {
            name: "first_name",
            type: "text",
            placeholder: "First Name",
            label: "First Name",
            required: true,
        },
        {
            name: "last_name",
            type: "text",
            placeholder: "Last Name",
            label: "Last Name",
            required: true,
        },
        {
            name: "email",
            type: "email",
            placeholder: "E-Mail",
            label: "Email",
            required: true,
        },
        {
            name: "phone_number",
            type: "tel",
            placeholder: "Phone Number",
            label: "Phone Number",
            id: 'phone_number_code',
            required: true,
        },
        {
            name: "company_name",
            type: "text",
            placeholder: "Company Name",
            label: "Company Name",
            required: true,
        },
        {
            name: "industry_focus",
            type: "text",
            placeholder: "Industry Focus",
            label: "Industry Focus",
            required: true,
        },
        {
            name: "size",
            type: "number",
            placeholder: "Size",
            label: "Size",
            required: true,
        },
        {
            name: "website",
            type: "text",
            placeholder: "Website",
            label: "Website",
            required: true,
        },
        {
            name: "country_region",
            type: "select",
            placeholder: "Country / Region",
            label: "Country / Region",
            options: [
                { value: "", label: "Select Country" },
                { value: "United States of America", label: "United States of America" },
                { value: "United Kingdom", label: "United Kingdom" },
                { value: "Canada", label: "Canada" },
            ],
            required: true,
        },
        {
            name: "state",
            type: "select",
            placeholder: "Select State",
            label: "State",
            options: [{ value: "", label: "Select Country" },],
            required: true,
        },
        {
            name: "address1",
            type: "text",
            placeholder: "Office Address",
            label: "Office Address",
            required: true,
        },
        {
            name: "address2",
            type: "text",
            placeholder: "Office Address Line 2",
            label: "Office Address Line 2",
            required: false,
        },
        {
            name: "pin_code",
            type: "text",
            placeholder: "Zip Code",
            label: "Zip Code",
            required: true,
        },
        {
            name: "agree_to_terms",
            type: "checkbox",
            label: (
                <span>
                    I confirm that the information provided is accurate and I agree to the{" "}
                    <Link to="/terms-of-service" target="_blank">
                        Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy-policy" target="_blank">
                        Privacy & Policy
                    </Link>
                </span>
            ),
            required: true,
        },
    ];

    const validationFunc = (values) => {
        return validateFields(values, customFields);
    };

    const handleSubmit = (event, values, mobileCode) => {
        event.preventDefault();

        const allTouched = customFields.reduce((acc, field) => {
            acc[field.name] = true;
            return acc;
        }, {});
        setTouched(allTouched);

        setLoading(true);
        const updatedValues = {
            ...values
        };
        const { isValid, errors } = validateFields(updatedValues, customFields);

        if (isValid) {
            setErrors({});
            setFormValues(updatedValues);
            setStep(2);
            setLoading(false);
        } else {
            setErrors(errors);
            scrollToError(errors);
            setLoading(false);
        }

    }



    return (
        <>
            <div style={{ display: step === 1 ? "block" : "none" }}>
                <AuthForm
                    formType="New Business Profile"
                    initialValues={{
                        first_name: "",
                        last_name: "",
                        email: "",
                        phone_number: "",
                        company_name: "",
                        industry_focus: "",
                        size: 0,
                        website: "",
                        country_region: "",
                        state: "",
                        address1: "",
                        address2: "",
                        pin_code: "",
                        agree_to_terms: false,
                    }}
                    onSubmit={handleSubmit}
                    buttonLabel="Create"
                    loadingLabel="Signing Up..."
                    customLinks={[{ to: "/login", label: "Already have an account? Log-In" }]}
                    customFields={customFields}
                    isCustomSubmitFunction={true}
                    customFunctionErrors={errors}
                    stepper={true}
                    step={step}
                    loading={loading}
                    validationFunc={validationFunc}
                    touched={touched}
                    setTouched={setTouched}
                    subTitle = "Join us — it only takes a minute"
                />
            </div>

            <div style={{ display: step === 2 ? "block" : "none" }}>
                <InviteMembers step={step} setStep={setStep}
                    members={members} setMembers={setMembers}
                    formValues={formValues}
                />
            </div>

            <div style={{ display: step === 3 ? "block" : "none" }}>
                <div className="min-vh-100">
                    <Container fluid >
                        <Row className="min-vh-100">
                            <Col
                                md={4}
                                className="d-none d-md-flex align-items-center justify-content-center text-white p-[10px]"
                            >
                                <SignupLeftSideCard />
                            </Col>
                            <Col xs={12} md={8}  >
                                <div className="d-flex flex-column align-items-start justify-content-center ml-12">
                                    <div className="flex flex-col items-start text-center md:text-left">
                                        <div className="flex items-center mb-4 mt-4">
                                            <Stepper currentStep={step} />
                                        </div>

                                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-medium mb-2">
                                            Business Structure
                                        </h3>
                                        <p className="text-muted text-sm md:text-base mb-7">
                                            Set hierarchy for your team members
                                        </p>
                                    </div>
                                </div>

                                <CustomOrgChart title={"Business Structure"} formValues={formValues}
                                    setStep={setStep} members={members} />
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
        </>
    );
};

export default BusinessRegisterForm;
